import { RMWCanvasProps } from 'business/models/CustomModels';
import { useCanvasMagnifier } from 'components/canvas/hooks';
import MagnifyingRectangle from 'components/canvas/magnifyingRectangle';
import CanvasTooltip, { CanvasTooltipProps } from 'components/canvas/tooltip';
import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer } from 'react-konva';

const defaultTooltipState = {
  windowPointerPositionX: 0,
  windowPointerPositionY: 0,
  scale: 1,
  stage: null,
  toolTipText: null,
};
export default function ExtendedCanvas(props: RMWCanvasProps) {
  const {
    width,
    height,
    canvasStageId,
    canvasStageContainerId,
    onWheel,
    isZoomLocked,
    wellSelectionContent = null,
    canvasWidth,
    canvasHeight,
    handleZoomAreaSelected,
  } = props;

  const canvasRef = useRef<HTMLInputElement>();
  const stageRef = useRef(null);
  const layerRef = useRef(null);
  const layerRef2 = useRef(null);
  const errorLayerIconRef = useRef(null);
  const errorLayerRef = useRef(null);
  const [tooltipData, setTooltipData] = useState<CanvasTooltipProps>(defaultTooltipState);

  const {
    onMouseDown: canvasMagnifierOnMouseDown,
    onMouseMove: canvasMagnifierOnMouseMove,
    onMouseUp: canvasMagnifierOnMouseUp,
    selectionRectangle,
  } = useCanvasMagnifier(stageRef, handleZoomAreaSelected);

  function dragBoundFunc(pos) {
    const stage = stageRef.current;
    const scale = stage.scaleX();
    if (scale === 1) {
      return { x: 0, y: 0 };
    }
    if (pos.x > 0) {
      pos.x = 0;
    }
    if (pos.y > 0) {
      pos.y = 0;
    }
    return pos;
  }

  useEffect(() => {
    if (props.stageRef !== undefined && stageRef?.current) {
      props.stageRef(
        stageRef.current,
        layerRef.current,
        layerRef2.current,
        errorLayerRef,
        errorLayerIconRef,
      );
    }
  }, [
    width,
    height,
    canvasWidth,
    stageRef.current,
    layerRef.current,
    layerRef2.current,
    errorLayerRef,
    errorLayerIconRef,
  ]);

  useEffect(
    () => () => {
      layerRef2.current = null;
      stageRef.current = null;
      errorLayerIconRef.current = null;
      errorLayerRef.current = null;
    },
    [],
  );

  function onClick(evt) {
    if (props.onClick !== undefined) {
      props.onClick(evt);
    }
  }

  function onDblClick(evt) {
    if (props.onDblClick !== undefined) {
      props.onDblClick(evt);
    }
  }

  function onDragOver(event) {
    event.preventDefault();
  }

  function onMouseDown(evt) {
    if (props.onMouseDown !== undefined) {
      props.onMouseDown(evt);
    }
    canvasMagnifierOnMouseDown(evt);
  }

  function onMouseEnter(evt) {
    if (props.onMouseEnter !== undefined) {
      props.onMouseEnter(evt);
    }
  }

  function onMouseLeave(evt) {
    if (props.onMouseLeave !== undefined) {
      props.onMouseLeave(evt);
    }
  }

  function onMouseUp(evt) {
    if (props.onMouseUp !== undefined) {
      props.onMouseUp(evt);
    }
    canvasMagnifierOnMouseUp(evt);
  }

  function onMouseMove(evt) {
    if (props.onMouseMove !== undefined) {
      props.onMouseMove(evt);
    }
    canvasMagnifierOnMouseMove(evt);

    if (evt.target.attrs.tooltipInfo) {
      const { x, y } = evt.target.getStage().getRelativePointerPosition();
      setTooltipData({
        windowPointerPositionX: x,
        windowPointerPositionY: y,
        scale: evt.target.getStage().scaleX(),
        stage: null,
        toolTipText: evt.target.attrs.tooltipInfo,
      });
    } else {
      tooltipData.toolTipText !== null && setTooltipData(defaultTooltipState);
    }
  }

  function onDrop(event, cat) {
    const stage = stageRef.current;
    const scale = stage.scaleX();
    const stageX = stage.x();
    const stageY = stage.y();

    const stagecontainer = document.getElementById('stagecontainer');

    if (!stage || !stagecontainer) {
      if (props.onDrop) {
        props.onDrop(event);
      }
      return;
    }

    const offSetleft = stagecontainer.getBoundingClientRect().left;
    const offSettop = stagecontainer.getBoundingClientRect().top;

    const { clientX } = event;
    const { clientY } = event;

    const isvalidX = clientX - (stageX + offSetleft);
    const isvalidY = clientY - (stageY + offSettop);

    const endX = stage.x() + width * scale + offSetleft;

    const endY = stage.y() + height * scale + offSettop;

    if (isvalidX < 0 || isvalidY < 0 || clientX > endX || clientY > endY) {
      return;
    }

    if (props.onDrop) {
      props.onDrop(event);
    }
  }

  return (
    <div
      id={canvasStageContainerId ? canvasStageContainerId : 'stagecontainer'}
      className="droppable"
      onDragOver={(event) => onDragOver(event)}
      onDrop={(event) => onDrop(event, 'Done')}
      style={{ margin: 'auto', marginLeft: 5 }}
      ref={canvasRef}
    >
      <Stage
        ref={stageRef}
        style={{ marginLeft: 5 }}
        id={canvasStageId ? canvasStageId : 'stage'}
        width={canvasWidth || window.innerWidth}
        height={Math.max(canvasHeight, window.innerHeight)}
        onWheel={!isZoomLocked ? onWheel : null}
        onClick={onClick}
        onDblClick={onDblClick}
        draggable={!props.isDrawingActive ? props.draggable : false}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        dragBoundFunc={dragBoundFunc}
      >
        <Layer id="extendedcanvas-stage-layer-children" ref={layerRef}>
          {props.children}
        </Layer>
        <Layer id="extendedcanvas-stage-layer-ref2" ref={layerRef2}></Layer>
        <Layer id="extendedcanvas-stage-well-selection-content">
          {wellSelectionContent}
        </Layer>
        <Layer
          id="extendedcanvas-stage-layer-canvas-tool-tip"
          opacity={props.showScoresToolTip === false ? 0 : 1}
        >
          <CanvasTooltip
            {...tooltipData}
            scale={tooltipData.scale}
            stage={stageRef.current}
          />
        </Layer>
        <MagnifyingRectangle magnifyingRectangle={selectionRectangle} />
      </Stage>
    </div>
  );
}
